/**
 * 列表导出 hook（list-export-operations，design D9/D11）。
 *
 * 统一任务模型消费入口：创建导出任务——同步通道（completed）立即下载；
 * 异步通道提示转任务 + 5s 轮询至终态（completed 下载 / failed 提示原因）。
 * 页面按 EXPORT 权限自行控制按钮显隐，hook 只负责交互流程。
 */
import { useCallback, useRef, useState } from 'react';
import { App } from 'antd';
import { exportsApi, type ExportTarget } from '../api/exports.api';

/** 轮询间隔（毫秒）。 */
const POLL_INTERVAL_MS = 5_000;

/** 轮询最长时间上限（毫秒），超时提示稍后在任务列表查看。 */
const POLL_TIMEOUT_MS = 10 * 60_000;

export function useListExport(target: ExportTarget) {
  const { message } = App.useApp();
  const [exporting, setExporting] = useState(false);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** 轮询任务状态至终态。 */
  const pollUntilDone = useCallback(
    (taskId: string, startedAt: number): void => {
      pollTimerRef.current = setTimeout(async () => {
        try {
          const status = await exportsApi.getExportStatus(taskId);
          if (status.status === 'completed') {
            await exportsApi.downloadExportFile(taskId);
            message.success(`导出完成，共 ${status.rowCount ?? 0} 行`);
            return;
          }
          if (status.status === 'failed') {
            message.error(`导出任务失败：${status.failReason ?? '未知原因'}`);
            return;
          }
          if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
            message.info('导出任务仍在进行，可稍后重试下载');
            return;
          }
          pollUntilDone(taskId, startedAt);
        } catch (err) {
          message.error(err instanceof Error ? err.message : '导出任务状态查询失败');
        }
      }, POLL_INTERVAL_MS);
    },
    [message],
  );

  /** 按当前筛选发起导出。 */
  const runExport = useCallback(
    async (filters: Record<string, unknown> = {}) => {
      setExporting(true);
      try {
        const result = await exportsApi.createExport(target, filters);
        if (result.status === 'completed') {
          await exportsApi.downloadExportFile(result.taskId);
          message.success(`导出完成，共 ${result.rowCount ?? 0} 行`);
        } else {
          message.info('数据量较大，已转为导出任务，完成后将自动下载');
          pollUntilDone(result.taskId, Date.now());
        }
      } catch (err) {
        message.error(err instanceof Error ? err.message : '导出失败');
      } finally {
        setExporting(false);
      }
    },
    [target, message, pollUntilDone],
  );

  return { exporting, runExport };
}
